import { ArrowDownward, ArrowUpward, Today } from '@mui/icons-material'
import { IconButton, Skeleton, TextField, Typography } from '@mui/material'
import { Query } from '@promoboxx/graphql-gateway-types'
import localStorageStore from '@promoboxx/use-filter/dist/store/localStorageStore'
import useSimpleFilter from '@promoboxx/use-filter/dist/useSimpleFilter'
import I18n from 'i18n-js'
import moment from 'moment'
import { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'

import { OrganicCampaignDetailsFilters } from '@src/components/content/organicCampaignDetails/organicCampaignDetailsFilter'
import {
  ALL_TABS,
  formatDaysRemainingCopy,
} from '@src/components/content/organicCampaignDetails/OrganicCampaignDetailsLayout'
import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import FilterToolbar from '@src/components/shared/FilterToolbar'
import cloudinaryRef from '@src/lib/Cloudinary'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

import publicCampaignPaths from './publicCampaignPaths'
import PublicDownloadables from './PublicDownloadables'
import PublicShareSettingsCard from './PublicShareSettingsCard'
// Nearly identical to OrganicCampaignDetailsLayout, but with a few logic changes
const PublicCampaignViewLayout: React.FC<{
  campaign: Query['getCampaign']
}> = ({ campaign }) => {
  const params = publicCampaignPaths.publicDetails.useParams()
  const history = useHistory()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const activateToken = queryParams.get('access_activation_token')
  const enrollmentUrl =
    campaign?.registration_keys?.find((el) => el?.default)?.registration_url ||
    `/enroll/${campaign?.brand?.slug}/${campaign?.registration_keys?.[0]
      ?.link_id}${
      activateToken ? `?access_activation_token=${activateToken}` : ''
    }`
  const validTabs = useMemo(() => {
    const validChannels = campaign?.share_settings_count_per_share_type?.map(
      (x) => x.channel,
    )

    return ALL_TABS.filter(
      (x) => x.label === 'downloadables' || validChannels?.includes(x.value),
    )
  }, [campaign?.share_settings_count_per_share_type])

  const loadedShareSettings = useMemo(() => {
    if (!campaign) {
      return []
    }

    return (
      campaign?.share_settings?.filter(
        (setting) => setting.share_type === params.channel,
      ) || []
    )
  }, [campaign, params.channel])

  const redirectToEnrollment = () => {
    history.push(enrollmentUrl)
  }

  useEffect(() => {
    if (!campaign) {
      return
    }

    const firstChannel = validTabs[0]

    history.replace(
      publicCampaignPaths.publicDetails.build({
        campaignId: params.campaignId,
        channel: firstChannel?.label || '',
      }),
    )
  }, [campaign, history, params.campaignId, validTabs])

  const filterApi: OrganicCampaignDetailsFilters = useSimpleFilter(
    'shareSettings',
    {
      defaultFilterInfo: {
        filter: {
          sortField: 'updated',
          sortDirection: 'desc',
        },
      },
      store: localStorageStore,
    },
  )

  return (
    <>
      <FilterContentLayoutWrapper
        title={!campaign ? <Skeleton /> : campaign?.name || ''}
        description={
          !campaign ? (
            <>
              <Skeleton width="10%" />
              <Skeleton width="15%" />
              <Skeleton width="40%" />
            </>
          ) : (
            <>
              <Typography color="text.secondary" variant="body2">
                {campaign?.brand?.name}
                <br />
                <Today
                  fontSize="inherit"
                  color="inherit"
                  sx={{ verticalAlign: 'middle' }}
                />{' '}
                {formatDaysRemainingCopy(
                  new Date(),
                  moment(campaign?.end_date),
                )}
              </Typography>

              <Typography>{campaign?.description}</Typography>
            </>
          )
        }
        left={
          !campaign ? (
            <Skeleton width={180} height={100} />
          ) : (
            <>
              <img
                alt={campaign?.name || ''}
                src={cloudinaryRef.getCloudinaryUrl(
                  campaign?.logo_public_id || '',
                  {
                    background: 'auto:predominant',
                    crop: 'pad',
                    width: 180 * 2,
                    height: 100 * 2,
                    fetch_format: 'jpg',
                    quality: 'auto',
                  },
                )}
                style={{
                  flexBasis: '180px',
                  width: 180,
                  height: 100,
                }}
              />
            </>
          )
        }
        slot={{
          filter: (
            <>
              <FilterToolbar
                filterItems={[
                  <TextField
                    select
                    fullWidth
                    value={filterApi.filterInfo.filter.sortField}
                    label="Sort by ..."
                    onChange={redirectToEnrollment}
                  >
                    <MenuItemWithCheckbox value="created">
                      {I18n.t('filters.created')}
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="updated">
                      {I18n.t('filters.lastUpdated')}
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="name">
                      {I18n.t('filters.name')}
                    </MenuItemWithCheckbox>
                    <MenuItemWithCheckbox value="format">
                      {I18n.t('filters.format')}
                    </MenuItemWithCheckbox>
                  </TextField>,

                  <IconButton size="small" onClick={redirectToEnrollment}>
                    {filterApi.filterInfo.filter.sortDirection === 'desc' ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </IconButton>,
                ]}
                onClear={redirectToEnrollment}
              />
            </>
          ),
        }}
        slotProps={{
          tab: {
            tabs: validTabs.map((x) => {
              return {
                selectedTab: params.channel,
                value: x.label,
                label: x.label,
                to: enrollmentUrl,
              }
            }),
          },
        }}
      >
        {params.channel === 'downloadables' || params.channel === 'tiktok' ? (
          <PublicDownloadables
            shareType={params.channel}
            assets={campaign?.assets || []}
            filterApi={filterApi}
            onClickHijack={redirectToEnrollment}
          />
        ) : params.channel ? (
          <PublicShareSettingsCard
            campaignId={params.campaignId}
            shareType={params.channel}
            filterApi={filterApi}
            settings={loadedShareSettings}
            onClickHijack={redirectToEnrollment}
          />
        ) : undefined}
      </FilterContentLayoutWrapper>
    </>
  )
}

export default PublicCampaignViewLayout
